import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { instance } from "../../Const/ApiHeader";
import { USER_SUBSCRIPTION_RECORDS } from "../../Const/ApiConst";
import {
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Link } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAmount(amount, currency) {
  if (!amount) return "-";
  const code = currency || "INR";
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) {
    return `${code} ${amount}`;
  }
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    return `${code} ${numeric.toFixed(2)}`;
  }
}

function formatBillingPeriod(period, days) {
  if (period) {
    const map = {
      P1W: "Weekly",
      P1M: "Monthly",
      P3M: "Every 3 months",
      P6M: "Every 6 months",
      P1Y: "Yearly",
    };
    return map[period.toUpperCase()] || period;
  }
  if (days) return `${days} day(s)`;
  return "-";
}

function truncateMiddle(value, head = 10, tail = 8) {
  const text = String(value || "");
  if (!text) return "-";
  if (text.length <= head + tail + 3) return text;
  return `${text.slice(0, head)}…${text.slice(-tail)}`;
}

function DetailItem({ label, value, mono = false }) {
  return (
    <div className="sub-payment-detail">
      <span className="sub-payment-detail-label">{label}</span>
      <span
        className={
          mono ? "sub-payment-detail-value is-mono" : "sub-payment-detail-value"
        }
        title={typeof value === "string" ? value : undefined}
      >
        {value || "-"}
      </span>
    </div>
  );
}

function PaymentDetailsPanel({ record }) {
  return (
    <div className="sub-payment-details-grid">
      <DetailItem label="Order ID" value={record.orderId} mono />
      <DetailItem label="Product ID" value={record.productId} mono />
      <DetailItem label="Base plan" value={record.basePlanId} mono />
      <DetailItem label="Offer ID" value={record.offerId} mono />
      <DetailItem
        label="Amount"
        value={formatAmount(record.amount, record.currency)}
      />
      <DetailItem
        label="Billing period"
        value={formatBillingPeriod(record.billingPeriod, record.planValidity)}
      />
      <DetailItem label="Package" value={record.packageName} mono />
      <DetailItem label="Country" value={record.countryCode} />
      <DetailItem label="Play state" value={record.subscriptionState} />
      <DetailItem label="Ack state" value={record.acknowledgementState} />
      <DetailItem
        label="Auto-renewing"
        value={
          typeof record.autoRenewing === "boolean"
            ? record.autoRenewing
              ? "Yes"
              : "No"
            : "-"
        }
      />
      <DetailItem
        label="Start time"
        value={formatDateTime(record.startTime || record.paymentDate)}
      />
      <DetailItem
        label="Expiry time"
        value={formatDateTime(record.expiryDate)}
      />
      <DetailItem
        label="Purchase token"
        value={truncateMiddle(record.purchaseToken, 14, 10)}
        mono
      />
      <DetailItem label="User ID" value={record.cust_id} mono />
      <DetailItem label="Payment state" value={record.paymentState} />
    </div>
  );
}

function UserSubscriptionRecords() {
  const [userRecords, setUserRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [expandedId, setExpandedId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    filtered: 0,
  });
  const [planOptions, setPlanOptions] = useState([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadUserRecords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await instance.get(USER_SUBSCRIPTION_RECORDS, {
        params: {
          page,
          limit,
          search,
          status: statusFilter,
          plan: planFilter,
          paymentMethod: paymentMethodFilter,
        },
      });

      const payload = response?.data || {};
      const records = Array.isArray(payload) ? payload : payload.data || [];

      setUserRecords(records);
      if (!Array.isArray(payload)) {
        setPagination(
          payload.pagination || {
            page,
            limit,
            total: records.length,
            totalPages: 1,
            hasNextPage: false,
            hasPrevPage: false,
          }
        );
        setStats(
          payload.stats || {
            total: records.length,
            active: 0,
            expired: 0,
            filtered: records.length,
          }
        );
        setPlanOptions(payload.filters?.plans || []);
        setPaymentMethodOptions(payload.filters?.paymentMethods || []);
      }
    } catch (error) {
      console.log(error);
      setUserRecords([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter, planFilter, paymentMethodFilter]);

  useEffect(() => {
    loadUserRecords();
  }, [loadUserRecords]);

  const rangeLabel = useMemo(() => {
    if (!pagination.total) return "0 records";
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(pagination.page * pagination.limit, pagination.total);
    return `${start}–${end} of ${pagination.total}`;
  }, [pagination]);

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handlePlanChange = (value) => {
    setPlanFilter(value);
    setPage(1);
  };

  const handlePaymentMethodChange = (value) => {
    setPaymentMethodFilter(value);
    setPage(1);
  };

  const handleLimitChange = (value) => {
    setLimit(Number(value));
    setPage(1);
  };

  const toggleExpanded = (id) => {
    setExpandedId((current) => (current === id ? null : id));
  };

  return (
    <div className="vehicles-page">
      <Breadcrumbs aria-label="breadcrumb" className="mb-3">
        <Link to="/">Home</Link>
        <Typography>Subscription Records</Typography>
      </Breadcrumbs>

      <section className="vehicles-hero">
        <div>
          <Typography className="vehicles-hero-eyebrow">
            <ReceiptLongOutlinedIcon fontSize="inherit" /> Payment history
          </Typography>
          <Typography variant="h4" className="vehicles-hero-title">
            Subscription Records
          </Typography>
          <Typography className="vehicles-hero-subtitle">
            Search and filter user subscription payments across plans and
            payment methods. Expand a row for Play Billing payment details.
          </Typography>
        </div>
        <Button component={Link} to="/subscriptions" variant="contained">
          Manage plans
        </Button>
      </section>

      <section className="vehicles-stats-grid">
        <div className="vehicles-stat-card">
          <Typography className="vehicles-stat-value">{stats.total}</Typography>
          <Typography className="vehicles-stat-label">Total records</Typography>
        </div>
        <div className="vehicles-stat-card tone-green">
          <Typography className="vehicles-stat-value">{stats.active}</Typography>
          <Typography className="vehicles-stat-label">Active</Typography>
        </div>
        <div className="vehicles-stat-card tone-muted">
          <Typography className="vehicles-stat-value">{stats.expired}</Typography>
          <Typography className="vehicles-stat-label">Expired</Typography>
        </div>
        <div className="vehicles-stat-card tone-blue">
          <Typography className="vehicles-stat-value">{stats.filtered}</Typography>
          <Typography className="vehicles-stat-label">Matching filters</Typography>
        </div>
      </section>

      <section className="section vehicles-toolbar">
        <Form.Control
          className="admin-search"
          type="search"
          placeholder="Search by name, email, contact, plan..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <Form.Select
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
          style={{ maxWidth: 160 }}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
        </Form.Select>
        <Form.Select
          value={planFilter}
          onChange={(e) => handlePlanChange(e.target.value)}
          style={{ maxWidth: 180 }}
        >
          <option value="all">All plans</option>
          {planOptions.map((plan) => (
            <option key={plan} value={plan}>
              {plan}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          value={paymentMethodFilter}
          onChange={(e) => handlePaymentMethodChange(e.target.value)}
          style={{ maxWidth: 180 }}
        >
          <option value="all">All payment methods</option>
          {paymentMethodOptions.map((method) => (
            <option key={method} value={method}>
              {method}
            </option>
          ))}
        </Form.Select>
      </section>

      <div className="section">
        <table className="table admin-table mt-0">
          <thead>
            <tr>
              <th style={{ width: 48 }} />
              <th>User</th>
              <th>Contact</th>
              <th>Plan</th>
              <th>Validity</th>
              <th>Amount</th>
              <th>Payment Method</th>
              <th>Order ID</th>
              <th>Payment Date</th>
              <th>Expiry Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={11} className="text-center py-4">
                  <CircularProgress size={28} />
                </td>
              </tr>
            ) : userRecords.length === 0 ? (
              <tr>
                <td colSpan={11} className="text-secondary">
                  {stats.total === 0
                    ? "No subscription payments recorded yet."
                    : "No records match your search or filters."}
                </td>
              </tr>
            ) : (
              userRecords.map((record) => {
                const isExpanded = expandedId === record._id;
                return (
                  <React.Fragment key={record._id}>
                    <tr
                      className={
                        isExpanded ? "sub-record-row is-expanded" : "sub-record-row"
                      }
                    >
                      <td>
                        <IconButton
                          size="small"
                          aria-label={
                            isExpanded
                              ? "Hide payment details"
                              : "Show payment details"
                          }
                          onClick={() => toggleExpanded(record._id)}
                        >
                          {isExpanded ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          )}
                        </IconButton>
                      </td>
                      <td>
                        <div>{record.fullName}</div>
                        <div className="text-secondary small">{record.email}</div>
                      </td>
                      <td>{record.contactNo}</td>
                      <td>
                        <div>{record.planName}</div>
                        {record.productId ? (
                          <div className="text-secondary small">
                            {record.productId}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <div>
                          {formatBillingPeriod(
                            record.billingPeriod,
                            record.planValidity
                          )}
                        </div>
                        {record.planValidity ? (
                          <div className="text-secondary small">
                            {record.planValidity} day(s) from Play
                          </div>
                        ) : null}
                      </td>
                      <td>{formatAmount(record.amount, record.currency)}</td>
                      <td>{record.paymentMethod || "Google Play"}</td>
                      <td className="sub-order-id">
                        {truncateMiddle(record.orderId, 8, 6)}
                      </td>
                      <td>{formatDate(record.paymentDate)}</td>
                      <td>{formatDate(record.expiryDate)}</td>
                      <td>
                        <Chip
                          size="small"
                          label={record.isLive ? "Active" : record.status}
                          color={record.isLive ? "success" : "default"}
                        />
                      </td>
                    </tr>
                    <tr className="sub-record-details-row">
                      <td colSpan={11} className="p-0 border-0">
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <div className="sub-payment-details-panel">
                            <Typography className="sub-payment-details-title">
                              Payment details
                            </Typography>
                            <PaymentDetailsPanel record={record} />
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>

        <div className="admin-pagination">
          <Typography className="admin-pagination-range">{rangeLabel}</Typography>

          <div className="admin-pagination-controls">
            <IconButton
              size="small"
              disabled={!pagination.hasPrevPage || loading}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              aria-label="Previous page"
            >
              <ChevronLeftIcon />
            </IconButton>

            <Pagination
              count={pagination.totalPages}
              page={pagination.page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="small"
              siblingCount={1}
              boundaryCount={1}
              disabled={loading}
            />

            <IconButton
              size="small"
              disabled={!pagination.hasNextPage || loading}
              onClick={() =>
                setPage((prev) => Math.min(pagination.totalPages, prev + 1))
              }
              aria-label="Next page"
            >
              <ChevronRightIcon />
            </IconButton>
          </div>

          <div className="admin-pagination-size">
            <Typography variant="body2" color="text.secondary">
              Rows
            </Typography>
            <Select
              size="small"
              value={limit}
              onChange={(e) => handleLimitChange(e.target.value)}
              disabled={loading}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserSubscriptionRecords;
